/**
 * Dev Mode Extraction Cache
 * =========================
 *
 * Provides localStorage-backed caching of contract structure extraction results
 * for rapid development iteration. Unlike the session-based cache in useStructureScanCache,
 * this cache:
 *
 * - Persists across page refreshes and browser sessions
 * - Has no TTL (keeps data until explicitly cleared)
 * - Provides mock data generation when no cache exists
 * - Is completely disabled in production builds
 *
 * USAGE:
 * 1. Complete one extraction normally during onboarding
 * 2. The result is automatically cached to localStorage
 * 3. Enable VITE_DEV_SKIP_EXTRACTION=true in .env.local
 * 4. Subsequent onboarding flows will use cached data instantly
 *
 * Alternatively, toggle "Skip Extraction" in DevTools without env var.
 *
 * @module devExtractionCache
 * @since v2.1.0
 */

import type {
  TemplateExtractionResult,
  ExtractedField,
  ExtractedMilestone,
} from '@/services/extraction/railway-client';

import { DEV_AUTH_CONFIG } from '@/config/devAuth';
import {
  STORAGE_KEYS,
  getStorageItem,
  setStorageItem,
  removeStorageItem,
} from '@/lib/storage';

// ============================================================================
// Constants
// ============================================================================

const CACHE_KEY = STORAGE_KEYS.DEV_EXTRACTION_CACHE;
const SKIP_EXTRACTION_KEY = STORAGE_KEYS.DEV_SKIP_EXTRACTION;
const CACHE_VERSION = 1;

// ============================================================================
// Types
// ============================================================================

interface CachedExtraction {
  version: number;
  timestamp: string;
  templateName: string;
  result: TemplateExtractionResult;
  fields: ExtractedField[];
  milestones: ExtractedMilestone[];
}

export interface DevExtractionData {
  result: TemplateExtractionResult;
  fields: ExtractedField[];
  milestones: ExtractedMilestone[];
  source: 'cache' | 'mock';
  templateName: string;
}

export interface DevExtractionCacheSummary {
  exists: boolean;
  templateName?: string;
  fieldsCount?: number;
  milestonesCount?: number;
  cachedAt?: string;
  age?: string;
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Check if dev extraction caching is available.
 * Only works in development mode.
 */
export function isDevMode(): boolean {
  return import.meta.env.DEV;
}

/**
 * Check if extraction skip is enabled.
 * Returns true if either:
 * - VITE_DEV_SKIP_EXTRACTION env var is set to 'true'
 * - Runtime toggle in DevTools is enabled (stored in localStorage)
 *
 * Always returns false in production.
 */
export function isDevSkipExtractionEnabled(): boolean {
  if (!import.meta.env.DEV) return false;

  // Check env var first (takes precedence)
  if (DEV_AUTH_CONFIG.skipExtraction) return true;

  const stored = getStorageItem<string>(SKIP_EXTRACTION_KEY);
  return stored === 'true';
}

/**
 * Cache an extraction result for later dev mode use.
 * Automatically called after successful extractions in onboarding.
 *
 * @param result - The full extraction result from Railway API
 * @param fields - Extracted fields array
 * @param milestones - Extracted milestones array
 * @param templateName - User-provided template name
 */
export function cacheDevExtraction(
  result: TemplateExtractionResult,
  fields: ExtractedField[],
  milestones: ExtractedMilestone[],
  templateName: string
): void {
  if (!import.meta.env.DEV) return;

  try {
    const cached: CachedExtraction = {
      version: CACHE_VERSION,
      timestamp: new Date().toISOString(),
      templateName,
      result,
      fields,
      milestones,
    };

    setStorageItem(CACHE_KEY, cached);

    console.log(
      '%c[DEV] Extraction cached for dev skip',
      'background: #4caf50; color: white; padding: 2px 6px; border-radius: 3px;',
      `\n  Template: ${templateName}`,
      `\n  Fields: ${fields.length}`,
      `\n  Milestones: ${milestones.length}`
    );
  } catch (error) {
    console.warn('[DEV] Failed to cache extraction result:', error);
  }
}

/**
 * Retrieve the cached extraction result.
 * Returns null if no cache exists or cache is invalid.
 */
export function getCachedDevExtraction(): CachedExtraction | null {
  if (!import.meta.env.DEV) return null;

  try {
    const raw = getStorageItem<string>(CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedExtraction;

    // Validate cache version
    if (cached.version !== CACHE_VERSION) {
      console.log('[DEV] Extraction cache version mismatch, clearing');
      clearDevExtractionCache();
      return null;
    }

    // Validate required fields
    if (!cached.result || !cached.fields || !cached.milestones) {
      console.log('[DEV] Extraction cache missing required data, clearing');
      clearDevExtractionCache();
      return null;
    }

    return cached;
  } catch (error) {
    console.warn('[DEV] Failed to read extraction cache:', error);
    return null;
  }
}

/**
 * Check if a valid extraction cache exists.
 */
export function hasDevExtractionCache(): boolean {
  return getCachedDevExtraction() !== null;
}

/**
 * Get a summary of the cached extraction for display in DevTools.
 */
export function getDevExtractionCacheSummary(): DevExtractionCacheSummary {
  const cached = getCachedDevExtraction();

  if (!cached) {
    return { exists: false };
  }

  // Calculate age
  const cachedDate = new Date(cached.timestamp);
  const now = new Date();
  const ageMs = now.getTime() - cachedDate.getTime();
  const ageMinutes = Math.floor(ageMs / (1000 * 60));
  const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));

  let age: string;
  if (ageDays > 0) {
    age = `${ageDays}d ago`;
  } else if (ageHours > 0) {
    age = `${ageHours}h ago`;
  } else if (ageMinutes > 0) {
    age = `${ageMinutes}m ago`;
  } else {
    age = 'just now';
  }

  return {
    exists: true,
    templateName: cached.templateName,
    fieldsCount: cached.fields.length,
    milestonesCount: cached.milestones.length,
    cachedAt: cached.timestamp,
    age,
  };
}

/**
 * Clear the extraction cache.
 */
export function clearDevExtractionCache(): void {
  if (!import.meta.env.DEV) return;

  removeStorageItem(CACHE_KEY);
  console.log(
    '%c[DEV] Extraction cache cleared',
    'background: #ff9800; color: black; padding: 2px 6px; border-radius: 3px;'
  );
}

// ============================================================================
// Mock Data Generator
// ============================================================================

/**
 * Create a mock extraction result for dev mode when no cache exists.
 * Uses realistic data structure that mirrors actual Railway API responses.
 */
export function createMockExtraction(): DevExtractionData {
  const fields: ExtractedField[] = [
    // Key fields (required for verification)
    {
      field_key: 'purchase_price',
      display_name: 'Purchase Price',
      field_type: 'number',
      is_critical: true,
      extraction_hints: 'The agreed sale price of the property',
    },
    {
      field_key: 'earnest_money',
      display_name: 'Earnest Money Deposit',
      field_type: 'number',
      is_critical: true,
      extraction_hints: 'Initial deposit amount',
    },
    {
      field_key: 'closing_date',
      display_name: 'Closing Date',
      field_type: 'date',
      is_critical: true,
      extraction_hints: 'Scheduled closing date for the transaction',
    },
    {
      field_key: 'property_address',
      display_name: 'Property Address',
      field_type: 'string',
      is_critical: true,
      extraction_hints: 'Full property address including city, state, zip',
    },
    {
      field_key: 'buyer_name',
      display_name: 'Buyer Name',
      field_type: 'string',
      is_critical: true,
      extraction_hints: 'Full legal name of the buyer(s)',
    },
    {
      field_key: 'seller_name',
      display_name: 'Seller Name',
      field_type: 'string',
      is_critical: true,
      extraction_hints: 'Full legal name of the seller(s)',
    },
    // Additional fields
    {
      field_key: 'contract_date',
      display_name: 'Contract Date',
      field_type: 'date',
      is_critical: false,
      extraction_hints: 'Date the contract was signed',
    },
    {
      field_key: 'financing_type',
      display_name: 'Financing Type',
      field_type: 'string',
      is_critical: false,
      extraction_hints: 'Type of financing (conventional, FHA, VA, cash)',
    },
    {
      field_key: 'inspection_deadline',
      display_name: 'Inspection Deadline',
      field_type: 'date',
      is_critical: false,
      extraction_hints: 'Deadline for completing inspections',
    },
    {
      field_key: 'financing_contingency_deadline',
      display_name: 'Financing Contingency Deadline',
      field_type: 'date',
      is_critical: false,
      extraction_hints: 'Deadline for loan approval',
    },
    {
      field_key: 'appraisal_contingency_deadline',
      display_name: 'Appraisal Contingency Deadline',
      field_type: 'date',
      is_critical: false,
      extraction_hints: 'Deadline for appraisal completion',
    },
  ];

  const milestones: ExtractedMilestone[] = [
    {
      name: 'Contract Accepted',
      source_field: 'contract_date',
      calculation_type: 'exact_date',
      is_enabled_by_default: true,
      sort_order: 1,
      phase: 'intro',
    },
    {
      name: 'Earnest Money Due',
      source_field: 'earnest_money_due_date',
      calculation_type: 'days_after',
      base_field: 'contract_date',
      is_enabled_by_default: true,
      sort_order: 2,
      phase: 'intro',
    },
    {
      name: 'Inspection Deadline',
      source_field: 'inspection_deadline',
      calculation_type: 'exact_date',
      is_enabled_by_default: true,
      sort_order: 3,
      phase: 'milestone',
    },
    {
      name: 'Appraisal Ordered',
      source_field: 'appraisal_ordered_date',
      calculation_type: 'days_after',
      base_field: 'contract_date',
      is_enabled_by_default: true,
      sort_order: 4,
      phase: 'milestone',
    },
    {
      name: 'Financing Contingency',
      source_field: 'financing_contingency_deadline',
      calculation_type: 'exact_date',
      is_enabled_by_default: true,
      sort_order: 5,
      phase: 'milestone',
    },
    {
      name: 'Title Commitment',
      source_field: 'title_commitment_date',
      calculation_type: 'days_before',
      base_field: 'closing_date',
      is_enabled_by_default: true,
      sort_order: 6,
      phase: 'milestone',
    },
    {
      name: 'Final Walkthrough',
      source_field: 'final_walkthrough_date',
      calculation_type: 'days_before',
      base_field: 'closing_date',
      is_enabled_by_default: true,
      sort_order: 7,
      phase: 'closing',
    },
    {
      name: 'Closing',
      source_field: 'closing_date',
      calculation_type: 'exact_date',
      is_enabled_by_default: true,
      sort_order: 8,
      phase: 'closing',
    },
    {
      name: 'Possession Date',
      source_field: 'possession_date',
      calculation_type: 'exact_date',
      is_enabled_by_default: false,
      sort_order: 9,
      phase: 'post-closing',
    },
  ];

  const templateName = 'Dev Mode Template';

  const result: TemplateExtractionResult = {
    templateId: 'dev-mock-template-' + Date.now(),
    templateName,
    fieldsCount: fields.length,
    milestonesCount: milestones.length,
    validationWarnings: [],
    fields,
    milestones,
    metadata: {
      contract_type: 'Purchase Agreement',
      extracted_at: new Date().toISOString(),
      region: 'US-OH',
    },
  };

  return { result, fields, milestones, source: 'mock', templateName };
}

// ============================================================================
// Main API
// ============================================================================

/**
 * Get extraction data for dev mode - uses cache if available, otherwise mock.
 * Returns null if not in dev mode.
 *
 * @returns Extraction data with source indicator, or null in production
 */
export function getDevExtractionData(): DevExtractionData | null {
  if (!import.meta.env.DEV) return null;

  const cached = getCachedDevExtraction();

  if (cached) {
    console.log(
      '%c[DEV] Using cached extraction (skip enabled)',
      'background: #2196f3; color: white; padding: 2px 6px; border-radius: 3px;',
      `\n  Template: ${cached.templateName}`,
      `\n  Cached: ${cached.timestamp}`
    );
    return {
      result: cached.result,
      fields: cached.fields,
      milestones: cached.milestones,
      source: 'cache',
      templateName: cached.templateName,
    };
  }

  console.log(
    '%c[DEV] Using mock extraction (no cache available)',
    'background: #9c27b0; color: white; padding: 2px 6px; border-radius: 3px;'
  );
  return createMockExtraction();
}
